/**
 * GIS Viewer Component
 *
 * 2D/3D viewer for GIS files using Leaflet
 * Supports: KML, KMZ, Shapefile, GeoJSON, GPX, GML
 */

'use client';

import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { type DesignFile, type GISFeature, type GISLayer } from './types';
import { MapSkeleton, Skeleton } from '@/components/base/Skeleton';
import { TILE_URLS } from '@/config/mapTiles';

// Feature info for external tracking
export interface GISFeatureInfo {
  id: string;
  name: string;
  type: string;
  layerName: string;
  visible: boolean;
  properties: Record<string, any>;
}

// Feature type for grouping
export type GISFeatureType = 'Point' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon' | 'Other';

// Ref handle for parent component to control layers
export interface GISViewerHandle {
  toggleLayer: (subLayerId: string) => void;
  zoomToLayer: (subLayerId: string) => void;
  getSubLayers: () => Array<{ id: string; name: string; featureCount: number; visible: boolean }>;
  toggleFeature: (featureId: string) => void;
  zoomToFeature: (featureId: string) => void;
  getFeatures: () => GISFeatureInfo[];
  toggleFeaturesByType: (type: GISFeatureType, visible: boolean) => void;
  getFeatureTypeStats: () => Record<GISFeatureType, { total: number; visible: number }>;
}

// Layer info for parent component
export interface FileLayerInfo {
  fileId: string;
  fileName: string;
  layers: Array<{
    id: string;
    name: string;
    featureCount: number;
    visible: boolean;
  }>;
}

interface GISViewerProps {
  files: DesignFile[];
  selectedFileId?: string;
  onElementSelect?: (feature: GISFeature) => void;
  onLayersLoaded?: (layerInfo: FileLayerInfo) => void;
  onFeaturesLoaded?: (features: GISFeatureInfo[]) => void;
  onLayerToggle?: (fileId: string, layerId: string, visible: boolean) => void;
  onZoomToLayer?: (fileId: string, layerId: string) => void;
  height?: string | number;
  mode?: '2d' | '3d';
  initialCenter?: [number, number];
  initialZoom?: number;
}

// Dynamic imports
let L: any = null;
let omnivore: any = null;

const GISViewer = forwardRef<GISViewerHandle, GISViewerProps>(({
  files,
  selectedFileId,
  onElementSelect,
  onLayersLoaded,
  onFeaturesLoaded,
  onLayerToggle,
  onZoomToLayer,
  height = '100%',
  mode = '2d',
  initialCenter = [17.385, 78.4867], // Hyderabad default
  initialZoom = 12
}, ref) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layersRef = useRef<Map<string, any>>(new Map());
  // Track individual sub-layers (folders/groups within files) for toggling
  const subLayersRef = useRef<Map<string, { layer: any; name: string; featureCount: number; visible: boolean }>>(new Map());

  const [isLoading, setIsLoading] = useState(true);
  const [isLeafletLoaded, setIsLeafletLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<GISFeature | null>(null);
  const [layers, setLayers] = useState<GISLayer[]>([]);
  const [showLayerPanel, setShowLayerPanel] = useState(true);
  const [showFeaturePanel, setShowFeaturePanel] = useState(false);
  const [baseLayer, setBaseLayer] = useState<'osm' | 'satellite' | 'terrain'>('osm');
  const [featureSearch, setFeatureSearch] = useState('');
  const [boundsWarning, setBoundsWarning] = useState<string | null>(null);
  // Sub-layers state for UI (folders within KMZ files)
  const [subLayers, setSubLayers] = useState<Array<{
    id: string;
    fileId: string;
    name: string;
    featureCount: number;
    visible: boolean;
  }>>([]);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  // All loaded features for browsing
  const [allFeatures, setAllFeatures] = useState<Array<{
    id: string;
    name: string;
    type: string;
    layer: string;
    coordinates: any;
    bounds?: any;
    leafletLayer?: any;
    visible: boolean;
    properties: Record<string, any>;
  }>>([]);

  // Stats
  const [stats, setStats] = useState({ features: 0, layers: 0 });

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    toggleLayer: (subLayerId: string) => {
      const subLayerData = subLayersRef.current.get(subLayerId);
      if (!subLayerData || !mapRef.current) {
        console.warn('External toggle failed: layer not found', subLayerId);
        return;
      }

      // Use hasLayer to check actual map state
      const isCurrentlyOnMap = mapRef.current.hasLayer(subLayerData.layer);
      console.log(`External toggle "${subLayerData.name}": onMap=${isCurrentlyOnMap}`);

      if (isCurrentlyOnMap) {
        mapRef.current.removeLayer(subLayerData.layer);
      } else {
        subLayerData.layer.addTo(mapRef.current);
      }

      const newVisibility = !isCurrentlyOnMap;
      subLayersRef.current.set(subLayerId, { ...subLayerData, visible: newVisibility });
      setSubLayers(prev => prev.map(s =>
        s.id === subLayerId ? { ...s, visible: newVisibility } : s
      ));

      // Sync feature visibility with layer visibility
      const layerName = subLayerData.name;
      setAllFeatures(prev => prev.map(f => {
        if (f.layer.includes(layerName)) {
          return { ...f, visible: newVisibility };
        }
        return f;
      }));
    },
    zoomToLayer: (subLayerId: string) => {
      const subLayerData = subLayersRef.current.get(subLayerId);
      if (!subLayerData || !mapRef.current) return;

      const layer = subLayerData.layer;
      if (layer.getBounds && layer.getBounds().isValid()) {
        mapRef.current.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 16 });
      }
    },
    getSubLayers: () => {
      return Array.from(subLayersRef.current.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        featureCount: data.featureCount,
        visible: data.visible
      }));
    },
    toggleFeature: (featureId: string) => {
      setAllFeatures(prev => prev.map(f => {
        if (f.id === featureId) {
          const newVisible = !f.visible;
          // Toggle visibility on the leaflet layer
          if (f.leafletLayer && mapRef.current) {
            if (newVisible) {
              f.leafletLayer.addTo(mapRef.current);
            } else {
              mapRef.current.removeLayer(f.leafletLayer);
            }
          }
          return { ...f, visible: newVisible };
        }
        return f;
      }));
    },
    zoomToFeature: (featureId: string) => {
      const feature = allFeatures.find(f => f.id === featureId);
      if (!feature || !mapRef.current) return;

      if (feature.leafletLayer) {
        if (feature.leafletLayer.getBounds) {
          mapRef.current.fitBounds(feature.leafletLayer.getBounds(), { padding: [50, 50], maxZoom: 18 });
        } else if (feature.leafletLayer.getLatLng) {
          mapRef.current.setView(feature.leafletLayer.getLatLng(), 16);
        }
        if (feature.leafletLayer.openPopup) {
          feature.leafletLayer.openPopup();
        }
      }
    },
    getFeatures: () => {
      return allFeatures.map(f => ({
        id: f.id,
        name: f.name,
        type: f.type,
        layerName: f.layer,
        visible: f.visible,
        properties: f.properties || {}
      }));
    },
    toggleFeaturesByType: (type: GISFeatureType, visible: boolean) => {
      // Normalize type for matching (LineString and MultiLineString are both "lines")
      const matchesType = (featureType: string): boolean => {
        if (type === 'Point') return featureType === 'Point';
        if (type === 'LineString' || type === 'MultiLineString') {
          return featureType === 'LineString' || featureType === 'MultiLineString';
        }
        if (type === 'Polygon' || type === 'MultiPolygon') {
          return featureType === 'Polygon' || featureType === 'MultiPolygon';
        }
        return false;
      };

      setAllFeatures(prev => prev.map(f => {
        if (matchesType(f.type)) {
          // Toggle visibility on the leaflet layer
          if (f.leafletLayer && mapRef.current) {
            if (visible) {
              f.leafletLayer.addTo(mapRef.current);
            } else {
              mapRef.current.removeLayer(f.leafletLayer);
            }
          }
          return { ...f, visible };
        }
        return f;
      }));
    },
    getFeatureTypeStats: () => {
      const stats: Record<GISFeatureType, { total: number; visible: number }> = {
        Point: { total: 0, visible: 0 },
        LineString: { total: 0, visible: 0 },
        MultiLineString: { total: 0, visible: 0 },
        Polygon: { total: 0, visible: 0 },
        MultiPolygon: { total: 0, visible: 0 },
        Other: { total: 0, visible: 0 }
      };

      allFeatures.forEach(f => {
        const type = (f.type as GISFeatureType) || 'Other';
        if (stats[type]) {
          stats[type].total++;
          if (f.visible) stats[type].visible++;
        } else {
          stats.Other.total++;
          if (f.visible) stats.Other.visible++;
        }
      });

      return stats;
    }
  }), [allFeatures]);

  // Track reported files to avoid duplicate notifications
  const reportedFilesRef = useRef<Set<string>>(new Set());

  // Report features to parent when they change
  useEffect(() => {
    if (onFeaturesLoaded && allFeatures.length > 0) {
      const featureInfos: GISFeatureInfo[] = allFeatures.map(f => ({
        id: f.id,
        name: f.name,
        type: f.type,
        layerName: f.layer,
        visible: f.visible,
        properties: f.properties || {}
      }));
      onFeaturesLoaded(featureInfos);
    }
  }, [allFeatures, onFeaturesLoaded]);

  // Notify parent when layers are loaded (only for new files)
  useEffect(() => {
    if (!onLayersLoaded || subLayers.length === 0 || files.length === 0) return;

    // Group layers by file
    const layersByFile = new Map<string, typeof subLayers>();
    subLayers.forEach(layer => {
      if (!layersByFile.has(layer.fileId)) {
        layersByFile.set(layer.fileId, []);
      }
      layersByFile.get(layer.fileId)!.push(layer);
    });

    // Notify for each file that hasn't been reported yet
    layersByFile.forEach((layers, fileId) => {
      // Create a unique key based on fileId and layer count
      const reportKey = `${fileId}-${layers.length}`;
      if (reportedFilesRef.current.has(reportKey)) return;

      const file = files.find(f => f.id === fileId);
      if (file) {
        reportedFilesRef.current.add(reportKey);
        onLayersLoaded({
          fileId,
          fileName: file.name,
          layers: layers.map(l => ({
            id: l.id,
            name: l.name,
            featureCount: l.featureCount,
            visible: l.visible
          }))
        });
      }
    });
  }, [subLayers, files, onLayersLoaded]);

  // Andhra Pradesh approximate bounds for validation
  const AP_BOUNDS = {
    north: 19.5,
    south: 12.5,
    east: 84.5,
    west: 76.5
  };

  // Load Leaflet dynamically
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        const leaflet = await import('leaflet');
        L = leaflet.default;

        // Import Leaflet CSS
        await import('leaflet/dist/leaflet.css');

        // Fix default marker icons
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
          iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
          shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
        });

        setIsLeafletLoaded(true);
      } catch (err) {
        console.error('Failed to load Leaflet:', err);
        setError('Failed to load map viewer');
      }
    };

    loadLeaflet();
  }, []);

  // Track if map has been initialized
  const mapInitializedRef = useRef(false);

  // Initialize map - only once
  useEffect(() => {
    if (!isLeafletLoaded || !mapContainerRef.current) return;
    if (mapInitializedRef.current || mapRef.current) return;

    mapInitializedRef.current = true;

    // Create map
    const map = L.map(mapContainerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      zoomControl: false
    });

    // Add zoom control to top-right
    L.control.zoom({ position: 'topright' }).addTo(map);

    // Add scale
    L.control.scale({ position: 'bottomleft' }).addTo(map);

    mapRef.current = map;

    // Add base layer
    updateBaseLayer(baseLayer, map);

    setIsLoading(false);

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        mapInitializedRef.current = false;
      }
    };
  }, [isLeafletLoaded]); // Remove initialCenter/initialZoom from deps - they shouldn't change

  // Update base layer
  const updateBaseLayer = (type: 'osm' | 'satellite' | 'terrain', map?: any) => {
    const targetMap = map || mapRef.current;
    if (!targetMap) return;

    // Remove existing base layer
    targetMap.eachLayer((layer: any) => {
      if (layer._url && (layer._url.includes('tile') || layer._url.includes('openstreetmap') || layer._url.includes('google'))) {
        targetMap.removeLayer(layer);
      }
    });

    let tileLayer;

    switch (type) {
      case 'satellite':
        tileLayer = L.tileLayer(
          TILE_URLS.esriImagery,
          { attribution: 'Esri', maxZoom: 19 }
        );
        break;
      case 'terrain':
        tileLayer = L.tileLayer(
          TILE_URLS.openTopo,
          { attribution: 'OpenTopoMap', maxZoom: 17 }
        );
        break;
      default:
        tileLayer = L.tileLayer(
          TILE_URLS.osm,
          { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }
        );
    }

    tileLayer.addTo(targetMap);
    setBaseLayer(type);
  };

  // Clear features when files change (remove features from deleted files)
  useEffect(() => {
    const currentFileIds = new Set(files.map(f => f.id));
    setAllFeatures(prev => {
      // Keep only features that belong to current files
      const filtered = prev.filter(f => {
        // Extract fileId from feature layer name or id
        const matchesCurrentFile = files.some(file =>
          f.layer.includes(file.name) || f.id.includes(file.id)
        );
        return matchesCurrentFile || currentFileIds.size === 0;
      });
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [files]);

  // Load files
  useEffect(() => {
    if (!isLeafletLoaded || !mapRef.current) return;

    const loadFile = async (file: DesignFile) => {
      if (layersRef.current.has(file.id)) return;

      setIsLoading(true);

      try {
        const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        let layer: any;

        if (ext === '.geojson' || ext === '.json') {
          layer = await loadGeoJSON(file.url, file.name);
        } else if (ext === '.kml') {
          layer = await loadKML(file.url, file.name);
        } else if (ext === '.kmz') {
          layer = await loadKMZ(file.url, file.name, file.id);
        } else if (ext === '.gpx') {
          layer = await loadGPX(file.url, file.name);
        } else if (ext === '.shp' || ext === '.shx' || ext === '.dbf' || ext === '.prj') {
          layer = await loadShapefile(file.url, file.name);
        } else {
          // Try as GeoJSON
          layer = await loadGeoJSON(file.url, file.name);
        }

        if (layer) {
          // Only add to map if not already added (KMZ sub-layers are added individually)
          if (!layer._alreadyOnMap) {
            layer.addTo(mapRef.current);
          }
          layersRef.current.set(file.id, layer);

          // Fit bounds - with delay to ensure layer is rendered
          setTimeout(() => {
            if (layer.getBounds && mapRef.current) {
              const bounds = layer.getBounds();
              console.log('Fitting map to bounds:', bounds.isValid() ? {
                north: bounds.getNorth(),
                south: bounds.getSouth(),
                east: bounds.getEast(),
                west: bounds.getWest()
              } : 'invalid bounds');

              if (bounds.isValid()) {
                mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 18 });
              }
            }
          }, 100);

          // Add to layers state
          const newLayer: GISLayer = {
            id: file.id,
            name: file.name,
            visible: true,
            features: [],
            style: getDefaultStyle(ext)
          };
          setLayers(prev => [...prev.filter(l => l.id !== file.id), newLayer]);

          // Update stats
          updateStats();
        }
      } catch (err) {
        console.error('Error loading file:', file.name, err);
        setError(`Failed to load ${file.name}`);
      } finally {
        setIsLoading(false);
      }
    };

    // Load all files
    files.forEach(file => loadFile(file));
  }, [files, isLeafletLoaded]);

  // Load GeoJSON
  const loadGeoJSON = async (url: string, name: string): Promise<any> => {
    try {
      const response = await fetch(url);
      const data = await response.json();

      return L.geoJSON(data, {
        style: (feature: any) => getFeatureStyle(feature),
        pointToLayer: (feature: any, latlng: any) => {
          return L.circleMarker(latlng, {
            radius: 8,
            fillColor: '#4a90d9',
            color: '#fff',
            weight: 2,
            fillOpacity: 0.8
          });
        },
        onEachFeature: (feature: any, layer: any) => {
          setupFeatureInteraction(feature, layer, name);
        }
      });
    } catch (err) {
      console.error('Error loading GeoJSON:', err);
      return null;
    }
  };

  // Load KML
  const loadKML = async (url: string, name: string): Promise<any> => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const kml = parser.parseFromString(text, 'text/xml');

      // Check for parse errors
      const parseError = kml.querySelector('parsererror');
      if (parseError) {
        console.error('KML parse error:', parseError.textContent);
        throw new Error('Invalid KML file');
      }

      // Convert KML to GeoJSON
      const geojson = kmlToGeoJSON(kml);

      console.log(`KML loaded: ${name}, features: ${geojson.features.length}`);

      // Debug: log first feature with style info
      if (geojson.features.length > 0) {
        console.log('First feature properties:', geojson.features[0].properties);
      }

      return L.geoJSON(geojson, {
        style: (feature: any) => getFeatureStyle(feature),
        pointToLayer: (feature: any, latlng: any) => {
          const props = feature.properties || {};
          // Use marker color from KML if available
          if (props.markerColor) {
            return L.circleMarker(latlng, {
              radius: 8 * (props.markerScale || 1),
              fillColor: props.markerColor,
              color: '#fff',
              weight: 2,
              fillOpacity: 0.8
            });
          }
          return L.marker(latlng);
        },
        onEachFeature: (feature: any, layer: any) => {
          setupFeatureInteraction(feature, layer, name);
        }
      });
    } catch (err) {
      console.error('Error loading KML:', err);
      return null;
    }
  };

  // Load KMZ (KML in ZIP) - with support for embedded KMZ files and Folders
  const loadKMZ = async (url: string, name: string, fileId?: string): Promise<any> => {
    const currentFileId = fileId || name;
    try {
      // Fetch KMZ file as array buffer
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      // Try to extract KML from KMZ using JSZip (dynamically imported)
      try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(arrayBuffer);

        // Find the KML file in the archive (usually doc.kml or *.kml)
        let kmlContent: string | null = null;
        const kmlFiles = Object.keys(zip.files).filter(f => f.toLowerCase().endsWith('.kml'));

        if (kmlFiles.length > 0) {
          // Prefer doc.kml if it exists
          const kmlFile = kmlFiles.find(f => f.toLowerCase() === 'doc.kml') || kmlFiles[0];
          kmlContent = await zip.files[kmlFile].async('string');
        }

        // Check for embedded KMZ files
        const embeddedKmzFiles = Object.keys(zip.files).filter(f => f.toLowerCase().endsWith('.kmz'));
        console.log('KMZ contains:', {
          kmlFiles: kmlFiles.length,
          embeddedKmzFiles: embeddedKmzFiles.length,
          allFiles: Object.keys(zip.files)
        });

        // Collection of all layers (main + embedded)
        const allLayers: any[] = [];

        // Process embedded KMZ files first
        for (const embeddedKmzPath of embeddedKmzFiles) {
          try {
            console.log('Processing embedded KMZ:', embeddedKmzPath);
            const embeddedArrayBuffer = await zip.files[embeddedKmzPath].async('arraybuffer');
            const embeddedZip = await JSZip.loadAsync(embeddedArrayBuffer);

            // Find KML in embedded KMZ
            const embeddedKmlFiles = Object.keys(embeddedZip.files).filter(f => f.toLowerCase().endsWith('.kml'));
            if (embeddedKmlFiles.length > 0) {
              const embeddedKmlFile = embeddedKmlFiles.find(f => f.toLowerCase() === 'doc.kml') || embeddedKmlFiles[0];
              const embeddedKmlContent = await embeddedZip.files[embeddedKmlFile].async('string');

              const parser = new DOMParser();
              const embeddedKml = parser.parseFromString(embeddedKmlContent, 'text/xml');

              // Check for parse errors
              const parseError = embeddedKml.querySelector('parsererror');
              if (parseError) {
                console.warn('Parse error in embedded KMZ:', embeddedKmzPath, parseError.textContent);
                continue;
              }

              // Process embedded KML (including its GroundOverlays)
              const embeddedLayers = await processKMLDocument(embeddedKml, embeddedZip, path.basename(embeddedKmzPath), currentFileId);
              if (embeddedLayers.length > 0) {
                allLayers.push(...embeddedLayers);
                console.log(`Loaded ${embeddedLayers.length} layers from embedded KMZ: ${embeddedKmzPath}`);
              }
            }
          } catch (embeddedErr) {
            console.warn('Failed to process embedded KMZ:', embeddedKmzPath, embeddedErr);
          }
        }

        if (kmlContent) {
          const parser = new DOMParser();
          const kml = parser.parseFromString(kmlContent, 'text/xml');

          // Check for parse errors
          const parseError = kml.querySelector('parsererror');
          if (parseError) {
            console.error('KML parse error in KMZ:', parseError.textContent);
            throw new Error('Invalid KML inside KMZ');
          }

          // Check for NetworkLinks (links to other KML/KMZ files)
          const networkLinks = kml.getElementsByTagName('NetworkLink');
          if (networkLinks.length > 0) {
            console.log('KMZ contains NetworkLinks:', networkLinks.length);
            // Log NetworkLink URLs for debugging
            for (let i = 0; i < networkLinks.length; i++) {
              const href = networkLinks[i].getElementsByTagName('href')[0]?.textContent;
              console.log(`  NetworkLink ${i + 1}: ${href}`);
            }
          }

          // Process the main KML document
          const mainLayers = await processKMLDocument(kml, zip, name, currentFileId);
          allLayers.push(...mainLayers);
        }

        // Calculate combined bounds (layers are already added to map individually)
        if (allLayers.length > 0) {
          const combinedGroup = L.featureGroup(allLayers);
          const bounds = combinedGroup.getBounds();
          console.log(`KMZ fully loaded: ${name}, total layers: ${allLayers.length}`);
          console.log('Combined bounds:', bounds.isValid() ? {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          } : 'invalid bounds');

          // Return the group for bounds calculation only (layers already on map)
          // Mark it so loadFile doesn't add it again
          combinedGroup._alreadyOnMap = true;
          return combinedGroup;
        }
      } catch (zipError: any) {
        // JSZip not available or extraction failed, try fallback
        console.warn('JSZip extraction failed:', zipError.message);
      }

      // Return placeholder with message
      const marker = L.marker(initialCenter)
        .bindPopup(`<b>${name}</b><br><em>KMZ file</em><br><small>Install jszip package for full support:<br><code>npm install jszip</code></small>`);

      return marker;
    } catch (err) {
      console.error('Error loading KMZ:', err);
      return null;
    }
  };

  // Helper to get basename from path
  const path = {
    basename: (p: string) => p.split('/').pop()?.split('\\').pop() || p
  };

  // Process a KML document and return array of Leaflet layers
  const processKMLDocument = async (kml: Document, zip: any, layerName: string, fileId?: string): Promise<any[]> => {
    const layers: any[] = [];
    const currentFileId = fileId || layerName;

    // Check for GroundOverlay (image overlay) first
    const groundOverlays = kml.getElementsByTagName('GroundOverlay');
    if (groundOverlays.length > 0) {
      console.log(`Processing ${groundOverlays.length} GroundOverlay(s) in ${layerName}`);

      for (let i = 0; i < groundOverlays.length; i++) {
        const overlay = groundOverlays[i];
        const overlayName = overlay.getElementsByTagName('name')[0]?.textContent || `Overlay ${i + 1}`;
        const iconHref = overlay.getElementsByTagName('href')[0]?.textContent;
        const latLonBox = overlay.getElementsByTagName('LatLonBox')[0];

        if (latLonBox) {
          const north = parseFloat(latLonBox.getElementsByTagName('north')[0]?.textContent || '0');
          const south = parseFloat(latLonBox.getElementsByTagName('south')[0]?.textContent || '0');
          const east = parseFloat(latLonBox.getElementsByTagName('east')[0]?.textContent || '0');
          const west = parseFloat(latLonBox.getElementsByTagName('west')[0]?.textContent || '0');

          console.log(`GroundOverlay "${overlayName}" bounds:`, { north, south, east, west });

          // Try to find the image in the zip
          let imageUrl: string | null = null;
          if (iconHref && zip) {
            const imageFile = zip.files[iconHref] || zip.files[iconHref.replace(/^\//, '')];
            if (imageFile) {
              const imageBlob = await imageFile.async('blob');
              imageUrl = URL.createObjectURL(imageBlob);
            }
          }

          // Create image overlay or fallback to rectangle
          const bounds = L.latLngBounds([[south, west], [north, east]]);

          if (imageUrl) {
            const imageOverlay = L.imageOverlay(imageUrl, bounds, { opacity: 0.8 });
            layers.push(imageOverlay);
          } else {
            // Fallback: create a rectangle
            const rect = L.rectangle(bounds, {
              color: '#4a90d9',
              weight: 2,
              fillOpacity: 0.1
            });
            rect.bindPopup(`<b>${overlayName}</b><br><small>Image overlay (image not loaded)</small>`);
            layers.push(rect);
          }
        }
      }
    }

    // Parse KML Folders to create separate layers
    const folders = kml.getElementsByTagName('Folder');
    const processedPlacemarks = new Set<Element>();

    // Process each Folder
    for (let i = 0; i < folders.length; i++) {
      const folder = folders[i];
      const folderName = folder.getElementsByTagName('name')[0]?.textContent || `Folder ${i + 1}`;

      // Get direct Placemark children of this folder (not nested ones)
      const folderPlacemarks: Element[] = [];
      for (let j = 0; j < folder.childNodes.length; j++) {
        const child = folder.childNodes[j];
        if (child.nodeName === 'Placemark') {
          folderPlacemarks.push(child as Element);
          processedPlacemarks.add(child as Element);
        }
      }

      if (folderPlacemarks.length > 0) {
        const geojson = placemarkListToGeoJSON(folderPlacemarks, kml);
        console.log(`Folder "${folderName}": ${geojson.features.length} features`);

        // Validate and log features
        validateAndLogFeatures(geojson, `${layerName} - ${folderName}`);

        if (geojson.features.length > 0) {
          // Debug: Log first few raw coordinates
          if (geojson.features.length > 0) {
            console.log(`📍 Raw coordinates sample from "${folderName}":`);
            geojson.features.slice(0, 3).forEach((f: any, idx: number) => {
              const coords = f.geometry?.coordinates;
              const name = f.properties?.name || `Feature ${idx}`;
              if (coords) {
                if (f.geometry?.type === 'Point') {
                  console.log(`  ${name}: lon=${coords[0]}, lat=${coords[1]}`);
                } else if (Array.isArray(coords[0])) {
                  console.log(`  ${name}: first coord: lon=${coords[0][0]}, lat=${coords[0][1]}`);
                }
              }
            });
          }

          const geoLayer = L.geoJSON(geojson, {
            style: (feature: any) => getFeatureStyle(feature),
            pointToLayer: (feature: any, latlng: any) => {
              const props = feature.properties || {};
              return L.circleMarker(latlng, {
                radius: 8 * (props.markerScale || 1),
                fillColor: props.markerColor || '#e74c3c',
                color: '#fff',
                weight: 2,
                fillOpacity: 0.8
              });
            },
            onEachFeature: (feature: any, layer: any) => {
              setupFeatureInteraction(feature, layer, `${layerName} - ${folderName}`);
            }
          });

          // Add layer directly to map for individual toggle control
          if (mapRef.current) {
            geoLayer.addTo(mapRef.current);
          }

          layers.push(geoLayer);

          // Register as sub-layer for toggling
          const subLayerId = `${currentFileId}-${folderName}`;
          subLayersRef.current.set(subLayerId, {
            layer: geoLayer,
            name: folderName,
            featureCount: geojson.features.length,
            visible: true
          });
          setSubLayers(prev => [...prev.filter(s => s.id !== subLayerId), {
            id: subLayerId,
            fileId: currentFileId,
            name: folderName,
            featureCount: geojson.features.length,
            visible: true
          }]);
        }
      }
    }

    // Process remaining Placemarks not in folders
    const allPlacemarks = kml.getElementsByTagName('Placemark');
    const remainingPlacemarks: Element[] = [];
    for (let i = 0; i < allPlacemarks.length; i++) {
      if (!processedPlacemarks.has(allPlacemarks[i])) {
        remainingPlacemarks.push(allPlacemarks[i]);
      }
    }

    if (remainingPlacemarks.length > 0) {
      const geojson = placemarkListToGeoJSON(remainingPlacemarks, kml);
      const rootLayerName = 'Other Features';
      console.log(`Root level placemarks in "${layerName}": ${geojson.features.length} features`);

      if (geojson.features.length > 0) {
        const geoLayer = L.geoJSON(geojson, {
          style: (feature: any) => getFeatureStyle(feature),
          pointToLayer: (feature: any, latlng: any) => {
            const props = feature.properties || {};
            return L.circleMarker(latlng, {
              radius: 8 * (props.markerScale || 1),
              fillColor: props.markerColor || '#e74c3c',
              color: '#fff',
              weight: 2,
              fillOpacity: 0.8
            });
          },
          onEachFeature: (feature: any, layer: any) => {
            setupFeatureInteraction(feature, layer, layerName);
          }
        });

        // Add layer directly to map for individual toggle control
        if (mapRef.current) {
          geoLayer.addTo(mapRef.current);
        }

        layers.push(geoLayer);

        // Register as sub-layer for toggling
        const subLayerId = `${currentFileId}-${rootLayerName}`;
        subLayersRef.current.set(subLayerId, {
          layer: geoLayer,
          name: rootLayerName,
          featureCount: geojson.features.length,
          visible: true
        });
        setSubLayers(prev => [...prev.filter(s => s.id !== subLayerId), {
          id: subLayerId,
          fileId: currentFileId,
          name: rootLayerName,
          featureCount: geojson.features.length,
          visible: true
        }]);
      }
    }

    return layers;
  };

  // Convert a list of Placemark elements to GeoJSON
  const placemarkListToGeoJSON = (placemarks: Element[], kmlDoc: Document): any => {
    const geojson: any = {
      type: 'FeatureCollection',
      features: []
    };

    // Parse styles from the document
    const styles = parseKMLStyles(kmlDoc);

    for (let i = 0; i < placemarks.length; i++) {
      const placemark = placemarks[i];
      const feature: any = {
        type: 'Feature',
        properties: {},
        geometry: null
      };

      // Get name
      const nameEl = placemark.getElementsByTagName('name')[0];
      if (nameEl) feature.properties.name = nameEl.textContent;

      // Get description
      const descEl = placemark.getElementsByTagName('description')[0];
      if (descEl) feature.properties.description = descEl.textContent;

      // Get styleUrl and apply style properties
      const styleUrl = placemark.getElementsByTagName('styleUrl')[0]?.textContent;
      if (styleUrl && styles.has(styleUrl)) {
        const style = styles.get(styleUrl);
        Object.assign(feature.properties, style);
      }

      // Check for inline Style
      const inlineStyle = Array.from(placemark.childNodes).find(
        (n: any) => n.nodeName === 'Style'
      ) as Element | undefined;
      if (inlineStyle) {
        const lineStyle = inlineStyle.getElementsByTagName('LineStyle')[0];
        const polyStyle = inlineStyle.getElementsByTagName('PolyStyle')[0];
        if (lineStyle) {
          const color = lineStyle.getElementsByTagName('color')[0]?.textContent;
          const width = lineStyle.getElementsByTagName('width')[0]?.textContent;
          if (color) feature.properties.stroke = kmlColorToHex(color);
          if (width) feature.properties['stroke-width'] = parseFloat(width);
        }
        if (polyStyle) {
          const color = polyStyle.getElementsByTagName('color')[0]?.textContent;
          if (color) {
            feature.properties.fill = kmlColorToHex(color);
            feature.properties['fill-opacity'] = kmlColorToOpacity(color);
          }
        }
      }

      // Parse geometry
      const point = placemark.getElementsByTagName('Point')[0];
      const lineString = placemark.getElementsByTagName('LineString')[0];
      const polygon = placemark.getElementsByTagName('Polygon')[0];
      const multiGeometry = placemark.getElementsByTagName('MultiGeometry')[0];

      if (point) {
        const coordsText = point.getElementsByTagName('coordinates')[0]?.textContent?.trim();
        if (coordsText) {
          const coords = coordsText.split(',');
          feature.geometry = {
            type: 'Point',
            coordinates: [parseFloat(coords[0]), parseFloat(coords[1])]
          };
        }
      } else if (lineString) {
        const coordsText = lineString.getElementsByTagName('coordinates')[0]?.textContent?.trim();
        if (coordsText) {
          const coords = parseKMLCoordinates(coordsText);
          feature.geometry = {
            type: 'LineString',
            coordinates: coords
          };
        }
      } else if (polygon) {
        feature.geometry = parseKMLPolygon(polygon);
      } else if (multiGeometry) {
        const geometries: any[] = [];
        const polys = multiGeometry.getElementsByTagName('Polygon');

        for (let p = 0; p < polys.length; p++) {
          const geom = parseKMLPolygon(polys[p]);
          if (geom) geometries.push(geom);
        }

        if (geometries.length === 1) {
          feature.geometry = geometries[0];
        } else if (geometries.length > 1 && geometries[0]?.type === 'Polygon') {
          feature.geometry = {
            type: 'MultiPolygon',
            coordinates: geometries.map(g => g.coordinates)
          };
        }
      }

      if (feature.geometry) {
        geojson.features.push(feature);
      }
    }

    return geojson;
  };

  // Load GPX
  const loadGPX = async (url: string, name: string): Promise<any> => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const gpx = parser.parseFromString(text, 'text/xml');

      // Convert GPX to GeoJSON
      const geojson = gpxToGeoJSON(gpx);

      return L.geoJSON(geojson, {
        style: {
          color: '#ff6b6b',
          weight: 3,
          opacity: 0.8
        },
        pointToLayer: (feature: any, latlng: any) => {
          return L.circleMarker(latlng, {
            radius: 6,
            fillColor: '#ff6b6b',
            color: '#fff',
            weight: 2,
            fillOpacity: 0.8
          });
        },
        onEachFeature: (feature: any, layer: any) => {
          setupFeatureInteraction(feature, layer, name);
        }
      });
    } catch (err) {
      console.error('Error loading GPX:', err);
      return null;
    }
  };

  // Load Shapefile (placeholder - requires shapefile library)
  const loadShapefile = async (url: string, name: string): Promise<any> => {
    console.log('Shapefile loading requires shapefile library. URL:', url);

    // Create placeholder
    return L.circle(initialCenter, {
      radius: 500,
      color: '#9b59b6',
      fillColor: '#9b59b6',
      fillOpacity: 0.3
    }).bindPopup(`<b>${name}</b><br>Shapefile (requires shapefile library)`);
  };

  // Parse KML Style elements
  const parseKMLStyles = (kml: Document): Map<string, any> => {
    const styles = new Map<string, any>();

    // Parse Style elements
    const styleElements = kml.getElementsByTagName('Style');
    for (let i = 0; i < styleElements.length; i++) {
      const style = styleElements[i];
      const id = style.getAttribute('id');
      if (!id) continue;

      const parsedStyle: any = {};

      // LineStyle
      const lineStyle = style.getElementsByTagName('LineStyle')[0];
      if (lineStyle) {
        const color = lineStyle.getElementsByTagName('color')[0]?.textContent;
        const width = lineStyle.getElementsByTagName('width')[0]?.textContent;
        if (color) parsedStyle.stroke = kmlColorToHex(color);
        if (width) parsedStyle['stroke-width'] = parseFloat(width);
      }

      // PolyStyle
      const polyStyle = style.getElementsByTagName('PolyStyle')[0];
      if (polyStyle) {
        const color = polyStyle.getElementsByTagName('color')[0]?.textContent;
        const fill = polyStyle.getElementsByTagName('fill')[0]?.textContent;
        const outline = polyStyle.getElementsByTagName('outline')[0]?.textContent;
        if (color) {
          parsedStyle.fill = kmlColorToHex(color);
          parsedStyle['fill-opacity'] = kmlColorToOpacity(color);
        }
        if (fill === '0') parsedStyle['fill-opacity'] = 0;
        if (outline === '0') parsedStyle['stroke-opacity'] = 0;
      }

      // IconStyle (for points)
      const iconStyle = style.getElementsByTagName('IconStyle')[0];
      if (iconStyle) {
        const color = iconStyle.getElementsByTagName('color')[0]?.textContent;
        const scale = iconStyle.getElementsByTagName('scale')[0]?.textContent;
        if (color) parsedStyle.markerColor = kmlColorToHex(color);
        if (scale) parsedStyle.markerScale = parseFloat(scale);
      }

      styles.set('#' + id, parsedStyle);
    }

    // Parse StyleMap elements (reference normal/highlight styles)
    const styleMaps = kml.getElementsByTagName('StyleMap');
    for (let i = 0; i < styleMaps.length; i++) {
      const styleMap = styleMaps[i];
      const id = styleMap.getAttribute('id');
      if (!id) continue;

      // Get normal style URL
      const pairs = styleMap.getElementsByTagName('Pair');
      for (let j = 0; j < pairs.length; j++) {
        const pair = pairs[j];
        const key = pair.getElementsByTagName('key')[0]?.textContent;
        const styleUrl = pair.getElementsByTagName('styleUrl')[0]?.textContent;
        if (key === 'normal' && styleUrl && styles.has(styleUrl)) {
          styles.set('#' + id, styles.get(styleUrl));
          break;
        }
      }
    }

    return styles;
  };

  // Convert KML AABBGGRR color to hex
  const kmlColorToHex = (kmlColor: string): string => {
    if (!kmlColor || kmlColor.length < 8) return '#4a90d9';
    // KML color is AABBGGRR (alpha, blue, green, red)
    const blue = kmlColor.substring(2, 4);
    const green = kmlColor.substring(4, 6);
    const red = kmlColor.substring(6, 8);
    return `#${red}${green}${blue}`;
  };

  // Get opacity from KML color
  const kmlColorToOpacity = (kmlColor: string): number => {
    if (!kmlColor || kmlColor.length < 2) return 0.5;
    const alpha = parseInt(kmlColor.substring(0, 2), 16);
    return alpha / 255;
  };

  // Convert KML to GeoJSON
  const kmlToGeoJSON = (kml: Document): any => {
    const geojson: any = {
      type: 'FeatureCollection',
      features: []
    };

    // Parse styles first
    const styles = parseKMLStyles(kml);

    // Parse Placemarks (including nested in Folders)
    const placemarks = kml.getElementsByTagName('Placemark');

    for (let i = 0; i < placemarks.length; i++) {
      const placemark = placemarks[i];
      const feature: any = {
        type: 'Feature',
        properties: {},
        geometry: null
      };

      // Get name
      const nameEl = placemark.getElementsByTagName('name')[0];
      if (nameEl) feature.properties.name = nameEl.textContent;

      // Get description
      const descEl = placemark.getElementsByTagName('description')[0];
      if (descEl) feature.properties.description = descEl.textContent;

      // Get styleUrl and apply style properties
      const styleUrl = placemark.getElementsByTagName('styleUrl')[0]?.textContent;
      if (styleUrl && styles.has(styleUrl)) {
        const style = styles.get(styleUrl);
        Object.assign(feature.properties, style);
      }

      // Check for inline Style
      const inlineStyle = Array.from(placemark.childNodes).find(
        (n: any) => n.nodeName === 'Style'
      ) as Element | undefined;
      if (inlineStyle) {
        // Parse inline style
        const lineStyle = inlineStyle.getElementsByTagName('LineStyle')[0];
        const polyStyle = inlineStyle.getElementsByTagName('PolyStyle')[0];
        if (lineStyle) {
          const color = lineStyle.getElementsByTagName('color')[0]?.textContent;
          const width = lineStyle.getElementsByTagName('width')[0]?.textContent;
          if (color) feature.properties.stroke = kmlColorToHex(color);
          if (width) feature.properties['stroke-width'] = parseFloat(width);
        }
        if (polyStyle) {
          const color = polyStyle.getElementsByTagName('color')[0]?.textContent;
          if (color) {
            feature.properties.fill = kmlColorToHex(color);
            feature.properties['fill-opacity'] = kmlColorToOpacity(color);
          }
        }
      }

      // Parse geometry
      const point = placemark.getElementsByTagName('Point')[0];
      const lineString = placemark.getElementsByTagName('LineString')[0];
      const polygon = placemark.getElementsByTagName('Polygon')[0];
      const multiGeometry = placemark.getElementsByTagName('MultiGeometry')[0];

      if (point) {
        const coordsText = point.getElementsByTagName('coordinates')[0]?.textContent?.trim();
        if (coordsText) {
          const coords = coordsText.split(',');
          feature.geometry = {
            type: 'Point',
            coordinates: [parseFloat(coords[0]), parseFloat(coords[1])]
          };
        }
      } else if (lineString) {
        const coordsText = lineString.getElementsByTagName('coordinates')[0]?.textContent?.trim();
        if (coordsText) {
          const coords = parseKMLCoordinates(coordsText);
          feature.geometry = {
            type: 'LineString',
            coordinates: coords
          };
        }
      } else if (polygon) {
        feature.geometry = parseKMLPolygon(polygon);
      } else if (multiGeometry) {
        // Handle MultiGeometry
        const geometries: any[] = [];
        const points = multiGeometry.getElementsByTagName('Point');
        const lines = multiGeometry.getElementsByTagName('LineString');
        const polys = multiGeometry.getElementsByTagName('Polygon');

        // For simplicity, create multiple features
        for (let p = 0; p < polys.length; p++) {
          const geom = parseKMLPolygon(polys[p]);
          if (geom) geometries.push(geom);
        }

        if (geometries.length === 1) {
          feature.geometry = geometries[0];
        } else if (geometries.length > 1 && geometries[0]?.type === 'Polygon') {
          feature.geometry = {
            type: 'MultiPolygon',
            coordinates: geometries.map(g => g.coordinates)
          };
        }
      }

      if (feature.geometry) {
        geojson.features.push(feature);
      }
    }

    return geojson;
  };

  // Parse KML coordinates string to array
  const parseKMLCoordinates = (coordsText: string): number[][] => {
    return coordsText
      .trim()
      .split(/\s+/)
      .filter(c => c.length > 0)
      .map(c => {
        const parts = c.split(',');
        return [parseFloat(parts[0]), parseFloat(parts[1])];
      })
      .filter(c => !isNaN(c[0]) && !isNaN(c[1]));
  };

  // Parse KML Polygon element
  const parseKMLPolygon = (polygon: Element): any => {
    const outerBoundary = polygon.getElementsByTagName('outerBoundaryIs')[0];
    const coordsText = outerBoundary?.getElementsByTagName('coordinates')[0]?.textContent?.trim();

    if (!coordsText) return null;

    const outerRing = parseKMLCoordinates(coordsText);
    const coordinates: number[][][] = [outerRing];

    // Parse inner boundaries (holes)
    const innerBoundaries = polygon.getElementsByTagName('innerBoundaryIs');
    for (let i = 0; i < innerBoundaries.length; i++) {
      const innerCoordsText = innerBoundaries[i].getElementsByTagName('coordinates')[0]?.textContent?.trim();
      if (innerCoordsText) {
        coordinates.push(parseKMLCoordinates(innerCoordsText));
      }
    }

    return {
      type: 'Polygon',
      coordinates
    };
  };

  // Convert GPX to GeoJSON
  const gpxToGeoJSON = (gpx: Document): any => {
    const geojson: any = {
      type: 'FeatureCollection',
      features: []
    };

    // Parse waypoints
    const wpts = gpx.getElementsByTagName('wpt');
    for (let i = 0; i < wpts.length; i++) {
      const wpt = wpts[i];
      const lat = parseFloat(wpt.getAttribute('lat') || '0');
      const lon = parseFloat(wpt.getAttribute('lon') || '0');
      const name = wpt.getElementsByTagName('name')[0]?.textContent || `Waypoint ${i + 1}`;

      geojson.features.push({
        type: 'Feature',
        properties: { name, type: 'waypoint' },
        geometry: { type: 'Point', coordinates: [lon, lat] }
      });
    }

    // Parse tracks
    const trks = gpx.getElementsByTagName('trk');
    for (let i = 0; i < trks.length; i++) {
      const trk = trks[i];
      const name = trk.getElementsByTagName('name')[0]?.textContent || `Track ${i + 1}`;
      const trksegs = trk.getElementsByTagName('trkseg');

      for (let j = 0; j < trksegs.length; j++) {
        const trkseg = trksegs[j];
        const trkpts = trkseg.getElementsByTagName('trkpt');
        const coords: number[][] = [];

        for (let k = 0; k < trkpts.length; k++) {
          const trkpt = trkpts[k];
          const lat = parseFloat(trkpt.getAttribute('lat') || '0');
          const lon = parseFloat(trkpt.getAttribute('lon') || '0');
          coords.push([lon, lat]);
        }

        if (coords.length > 0) {
          geojson.features.push({
            type: 'Feature',
            properties: { name, type: 'track' },
            geometry: { type: 'LineString', coordinates: coords }
          });
        }
      }
    }

    return geojson;
  };

  // Get feature style
  const getFeatureStyle = (feature: any) => {
    const type = feature?.geometry?.type;
    const props = feature?.properties || {};

    // Use style from KML if available
    if (props.stroke) {
      return {
        color: props.stroke,
        weight: props['stroke-width'] || 2,
        opacity: props['stroke-opacity'] || 0.8,
        fillColor: props.fill || props.stroke,
        fillOpacity: props['fill-opacity'] || 0.3
      };
    }

    // Default styles by geometry type
    if (type === 'Polygon' || type === 'MultiPolygon') {
      return {
        color: '#4a90d9',
        weight: 2,
        opacity: 0.8,
        fillColor: '#4a90d9',
        fillOpacity: 0.3
      };
    }

    return {
      color: '#4a90d9',
      weight: 3,
      opacity: 0.8
    };
  };

  // Get default style
  const getDefaultStyle = (ext: string) => ({
    fillColor: ext === '.kml' ? '#e74c3c' : ext === '.gpx' ? '#ff6b6b' : '#4a90d9',
    strokeColor: ext === '.kml' ? '#c0392b' : ext === '.gpx' ? '#ff4757' : '#2980b9',
    fillOpacity: 0.3,
    strokeWidth: 2
  });

  // Setup feature interaction
  const setupFeatureInteraction = (feature: any, layer: any, layerName: string) => {
    // Generate a unique ID for this feature
    const featureId = feature.id || `${layerName}-${Math.random().toString(36).substr(2, 9)}`;

    // Add feature to allFeatures state for external tracking
    const featureInfo = {
      id: featureId,
      name: feature.properties?.name || 'Unnamed Feature',
      type: feature.geometry?.type || 'Unknown',
      layer: layerName,
      coordinates: feature.geometry?.coordinates || [],
      bounds: layer.getBounds ? layer.getBounds() : null,
      leafletLayer: layer,
      visible: true,
      properties: feature.properties || {}
    };

    setAllFeatures(prev => {
      // Avoid duplicates by checking if feature with same ID already exists
      if (prev.some(f => f.id === featureId)) {
        return prev;
      }
      return [...prev, featureInfo];
    });

    // Popup
    let popupContent = `<div class="p-2"><b>${layerName}</b>`;
    if (feature.properties) {
      const props = feature.properties;
      if (props.name) popupContent += `<br><b>Name:</b> ${props.name}`;
      if (props.description) popupContent += `<br>${props.description}`;

      // Show other properties
      Object.entries(props).forEach(([key, value]) => {
        if (!['name', 'description', 'styleUrl', 'styleHash'].includes(key) && value) {
          popupContent += `<br><b>${key}:</b> ${value}`;
        }
      });
    }
    popupContent += '</div>';
    layer.bindPopup(popupContent);

    // Click handler
    layer.on('click', () => {
      const gisFeature: GISFeature = {
        id: featureId,
        name: feature.properties?.name,
        type: feature.geometry?.type || 'Unknown',
        coordinates: feature.geometry?.coordinates || [],
        properties: feature.properties
      };
      setSelectedFeature(gisFeature);
      onElementSelect?.(gisFeature);
    });

    // Hover effects
    layer.on('mouseover', () => {
      if (layer.setStyle) {
        layer.setStyle({ weight: 4, fillOpacity: 0.5 });
      }
    });

    layer.on('mouseout', () => {
      if (layer.setStyle) {
        layer.setStyle(getFeatureStyle(feature));
      }
    });
  };

  // Toggle layer visibility (for files, toggle all sub-layers)
  const toggleLayerVisibility = (layerId: string) => {
    const layer = layersRef.current.get(layerId);
    if (!mapRef.current) return;

    // Get current visibility state from layers state
    const layerState = layers.find(l => l.id === layerId);
    const currentlyVisible = layerState?.visible ?? true;
    const newVisibility = !currentlyVisible;

    // Toggle all sub-layers belonging to this file
    const fileSubLayers = subLayers.filter(s => s.fileId === layerId);
    if (fileSubLayers.length > 0) {
      fileSubLayers.forEach(subLayer => {
        const subLayerData = subLayersRef.current.get(subLayer.id);
        if (subLayerData) {
          if (newVisibility) {
            subLayerData.layer.addTo(mapRef.current);
          } else {
            mapRef.current.removeLayer(subLayerData.layer);
          }
          subLayersRef.current.set(subLayer.id, { ...subLayerData, visible: newVisibility });
        }
      });

      // Update sub-layers state
      setSubLayers(prev => prev.map(s =>
        s.fileId === layerId ? { ...s, visible: newVisibility } : s
      ));
    } else if (layer) {
      // Fallback for non-KMZ files
      if (newVisibility) {
        layer.addTo(mapRef.current);
      } else {
        mapRef.current.removeLayer(layer);
      }
    }

    setLayers(prev => prev.map(l =>
      l.id === layerId ? { ...l, visible: newVisibility } : l
    ));
  };

  // Zoom to layer
  const zoomToLayer = (layerId: string) => {
    const layer = layersRef.current.get(layerId);
    if (!layer || !mapRef.current) return;

    if (layer.getBounds && layer.getBounds().isValid()) {
      mapRef.current.fitBounds(layer.getBounds(), { padding: [50, 50] });
    }
  };

  // Toggle sub-layer visibility - use hasLayer to check actual map state
  const toggleSubLayerVisibility = (subLayerId: string) => {
    const subLayerData = subLayersRef.current.get(subLayerId);
    if (!subLayerData || !mapRef.current) {
      console.warn('Toggle failed: subLayerData or map not found', { subLayerId, hasData: !!subLayerData, hasMap: !!mapRef.current });
      return;
    }

    // Use Leaflet's hasLayer to check actual state on map
    const isCurrentlyOnMap = mapRef.current.hasLayer(subLayerData.layer);
    console.log(`Toggling "${subLayerData.name}": actuallyOnMap=${isCurrentlyOnMap}, trackedVisible=${subLayerData.visible}`);

    if (isCurrentlyOnMap) {
      // Remove from map
      mapRef.current.removeLayer(subLayerData.layer);
      console.log(`  -> Removed "${subLayerData.name}" from map`);
    } else {
      // Add to map
      subLayerData.layer.addTo(mapRef.current);
      console.log(`  -> Added "${subLayerData.name}" to map`);
    }

    // New visibility is opposite of current map state
    const newVisibility = !isCurrentlyOnMap;

    // Update ref with new visibility
    subLayersRef.current.set(subLayerId, { ...subLayerData, visible: newVisibility });

    // Update state for UI
    setSubLayers(prev => prev.map(s =>
      s.id === subLayerId ? { ...s, visible: newVisibility } : s
    ));

    // Sync feature visibility with layer visibility
    // Features whose layer name contains this subLayer's name should be updated
    const layerName = subLayerData.name;
    setAllFeatures(prev => prev.map(f => {
      // Check if this feature belongs to the toggled layer
      if (f.layer.includes(layerName)) {
        return { ...f, visible: newVisibility };
      }
      return f;
    }));
  };

  // Expose toggle function for parent component
  const handleExternalToggle = (subLayerId: string) => {
    toggleSubLayerVisibility(subLayerId);
  };

  const handleExternalZoom = (subLayerId: string) => {
    zoomToSubLayer(subLayerId);
  };

  // Zoom to sub-layer
  const zoomToSubLayer = (subLayerId: string) => {
    const subLayerData = subLayersRef.current.get(subLayerId);
    if (!subLayerData || !mapRef.current) return;

    const layer = subLayerData.layer;
    if (layer.getBounds && layer.getBounds().isValid()) {
      mapRef.current.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 16 });
    }
  };

  // Toggle file expansion
  const toggleFileExpansion = (fileId: string) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Update stats
  const updateStats = () => {
    let totalFeatures = 0;

    layersRef.current.forEach(layer => {
      if (layer.getLayers) {
        totalFeatures += layer.getLayers().length;
      } else {
        totalFeatures += 1;
      }
    });

    setStats({
      features: totalFeatures,
      layers: layersRef.current.size
    });
  };

  // Check if coordinates are within expected bounds (Andhra Pradesh)
  const isWithinExpectedBounds = (coords: any): boolean => {
    if (!coords) return false;

    // Handle different coordinate formats
    let lng: number, lat: number;

    if (Array.isArray(coords)) {
      if (typeof coords[0] === 'number') {
        // [lng, lat] format
        lng = coords[0];
        lat = coords[1];
      } else if (Array.isArray(coords[0])) {
        // Polygon/LineString - check first coordinate
        return isWithinExpectedBounds(coords[0]);
      } else {
        return false;
      }
    } else {
      return false;
    }

    return lat >= AP_BOUNDS.south && lat <= AP_BOUNDS.north &&
           lng >= AP_BOUNDS.west && lng <= AP_BOUNDS.east;
  };

  // Get center of coordinates
  const getCoordsCenter = (coords: any): [number, number] | null => {
    if (!coords) return null;

    if (Array.isArray(coords)) {
      if (typeof coords[0] === 'number' && coords.length >= 2) {
        return [coords[1], coords[0]]; // [lat, lng]
      } else if (Array.isArray(coords[0])) {
        // For polygon/linestring, get centroid
        let sumLat = 0, sumLng = 0, count = 0;
        const flatCoords = coords.flat(10).filter((_, i) => i % 2 === 0 || i % 2 === 1);
        for (let i = 0; i < flatCoords.length - 1; i += 2) {
          sumLng += flatCoords[i];
          sumLat += flatCoords[i + 1];
          count++;
        }
        if (count > 0) {
          return [sumLat / count, sumLng / count];
        }
      }
    }
    return null;
  };

  // Validate bounds and identify outliers
  const validateAndLogFeatures = (geojson: any, layerName: string) => {
    const validFeatures: any[] = [];
    const outlierFeatures: any[] = [];

    geojson.features.forEach((feature: any, index: number) => {
      const coords = feature.geometry?.coordinates;
      const name = feature.properties?.name || `Feature ${index + 1}`;
      const center = getCoordsCenter(coords);

      if (center) {
        const [lat, lng] = center;
        const isValid = isWithinExpectedBounds(coords);

        if (!isValid) {
          outlierFeatures.push({
            name,
            type: feature.geometry?.type,
            lat,
            lng
          });
        } else {
          validFeatures.push(feature);
        }

        // Log features with coordinates (only first 5 and outliers)
        if (index < 5 || !isValid) {
          console.log(`Feature "${name}" (${feature.geometry?.type}): [${lat.toFixed(4)}, ${lng.toFixed(4)}] - ${isValid ? 'VALID (AP)' : 'OUTLIER'}`);
        }
      }
    });

    if (outlierFeatures.length > 0) {
      console.warn(`⚠️ Layer "${layerName}" has ${outlierFeatures.length}/${geojson.features.length} features OUTSIDE Andhra Pradesh:`);
      outlierFeatures.forEach(f => {
        console.warn(`  - "${f.name}" at [${f.lat.toFixed(2)}, ${f.lng.toFixed(2)}]`);
      });
      setBoundsWarning(`${outlierFeatures.length} features outside AP bounds. These may be sample data. Click "Zoom to Layer" in Layers panel.`);
    }

    console.log(`✓ Layer "${layerName}": ${validFeatures.length} features in AP, ${outlierFeatures.length} outliers`);

    return { validFeatures, outlierFeatures };
  };

  // Zoom to specific feature
  const zoomToFeature = (feature: typeof allFeatures[0]) => {
    if (!mapRef.current) return;

    if (feature.leafletLayer) {
      // If it's a layer with bounds
      if (feature.leafletLayer.getBounds) {
        mapRef.current.fitBounds(feature.leafletLayer.getBounds(), { padding: [50, 50], maxZoom: 18 });
      } else if (feature.leafletLayer.getLatLng) {
        // If it's a marker/point
        mapRef.current.setView(feature.leafletLayer.getLatLng(), 16);
      }
      // Open popup
      if (feature.leafletLayer.openPopup) {
        feature.leafletLayer.openPopup();
      }
    } else if (feature.coordinates) {
      const center = getCoordsCenter(feature.coordinates);
      if (center) {
        mapRef.current.setView(center, 16);
      }
    }
  };

  // Filter features based on search
  const filteredFeatures = allFeatures.filter(f =>
    f.name.toLowerCase().includes(featureSearch.toLowerCase()) ||
    f.layer.toLowerCase().includes(featureSearch.toLowerCase()) ||
    f.type.toLowerCase().includes(featureSearch.toLowerCase())
  );

  if (!isLeafletLoaded) {
    return (
      <MapSkeleton />
    );
  }

  return (
    <div className="relative w-full h-full" style={{ height }}>
      {/* Map Container */}
      <div ref={mapContainerRef} className="absolute inset-0 z-0" />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-20">
          <Skeleton shape="rounded" className="h-full w-full" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg text-sm z-30">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      )}

      {/* Bounds Warning */}
      {boundsWarning && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm z-30 flex items-center gap-3 max-w-md">
          <i className="ri-alert-line text-lg"></i>
          <span className="flex-1">{boundsWarning}</span>
          <button
            onClick={() => {
              // Zoom to Andhra Pradesh
              if (mapRef.current) {
                mapRef.current.fitBounds([
                  [AP_BOUNDS.south, AP_BOUNDS.west],
                  [AP_BOUNDS.north, AP_BOUNDS.east]
                ], { padding: [20, 20] });
              }
            }}
            className="bg-white/20 hover:bg-white/30 px-2 py-1 rounded text-xs whitespace-nowrap"
          >
            Zoom to AP
          </button>
          <button
            onClick={() => setBoundsWarning(null)}
            className="text-white/70 hover:text-white"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Base Layer Switcher */}
      <div className="absolute top-4 left-4 z-10">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <button
            onClick={() => updateBaseLayer('osm')}
            className={`px-3 py-2 text-xs ${baseLayer === 'osm' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            Map
          </button>
          <button
            onClick={() => updateBaseLayer('satellite')}
            className={`px-3 py-2 text-xs ${baseLayer === 'satellite' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            Satellite
          </button>
          <button
            onClick={() => updateBaseLayer('terrain')}
            className={`px-3 py-2 text-xs ${baseLayer === 'terrain' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            Terrain
          </button>
        </div>
      </div>

      {/* Layer Panel */}
      {showLayerPanel && (layers.length > 0 || subLayers.length > 0) && (
        <div className="absolute top-4 right-14 bg-white rounded-lg shadow-lg p-3 z-10 min-w-[240px] max-h-[400px] overflow-y-auto">
          <div className="flex items-center justify-between mb-2 pb-2 border-b">
            <span className="text-xs font-medium text-gray-700">Layers</span>
            <button
              onClick={() => setShowLayerPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>

          {/* Files with expandable sub-layers */}
          {layers.map(layer => {
            const fileSubLayers = subLayers.filter(s => s.fileId === layer.id);
            const isExpanded = expandedFiles.has(layer.id);
            const hasSubLayers = fileSubLayers.length > 0;

            return (
              <div key={layer.id} className="mb-1">
                {/* File header */}
                <div className="flex items-center justify-between py-1.5 hover:bg-gray-50 rounded">
                  <div className="flex items-center gap-1 flex-1">
                    {hasSubLayers && (
                      <button
                        onClick={() => toggleFileExpansion(layer.id)}
                        className="p-0.5 text-gray-400 hover:text-gray-600"
                      >
                        <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-sm`}></i>
                      </button>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={layer.visible}
                        onChange={() => toggleLayerVisibility(layer.id)}
                        className="rounded text-indigo-600"
                      />
                      <i className="ri-file-list-3-line text-teal-500 text-xs"></i>
                      <span className="text-xs text-gray-700 truncate max-w-[120px]" title={layer.name}>
                        {layer.name}
                      </span>
                    </label>
                  </div>
                  <button
                    onClick={() => zoomToLayer(layer.id)}
                    className="text-gray-400 hover:text-indigo-600 ml-1 p-1"
                    title="Zoom to file"
                  >
                    <i className="ri-focus-3-line text-sm"></i>
                  </button>
                </div>

                {/* Sub-layers (folders) */}
                {isExpanded && hasSubLayers && (
                  <div className="ml-5 border-l-2 border-gray-200 pl-2">
                    {fileSubLayers.map(subLayer => (
                      <div key={subLayer.id} className="flex items-center justify-between py-1 hover:bg-gray-50 rounded">
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={subLayer.visible}
                            onChange={() => toggleSubLayerVisibility(subLayer.id)}
                            className="rounded text-indigo-600 w-3.5 h-3.5"
                          />
                          <i className="ri-folder-line text-amber-500 text-xs"></i>
                          <span className="text-xs text-gray-600 truncate max-w-[100px]" title={subLayer.name}>
                            {subLayer.name}
                          </span>
                          <span className="text-[10px] text-gray-400">({subLayer.featureCount})</span>
                        </label>
                        <button
                          onClick={() => zoomToSubLayer(subLayer.id)}
                          className="text-gray-400 hover:text-indigo-600 p-0.5"
                          title={`Zoom to ${subLayer.name}`}
                        >
                          <i className="ri-focus-3-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Show sub-layers even if no parent layer entry */}
          {layers.length === 0 && subLayers.length > 0 && (
            <div className="text-xs text-gray-500">
              {subLayers.map(subLayer => (
                <div key={subLayer.id} className="flex items-center justify-between py-1.5">
                  <label className="flex items-center gap-2 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={subLayer.visible}
                      onChange={() => toggleSubLayerVisibility(subLayer.id)}
                      className="rounded text-indigo-600"
                    />
                    <span className="truncate max-w-[120px]">{subLayer.name}</span>
                    <span className="text-gray-400">({subLayer.featureCount})</span>
                  </label>
                  <button
                    onClick={() => zoomToSubLayer(subLayer.id)}
                    className="text-gray-400 hover:text-indigo-600 ml-2"
                    title="Zoom to layer"
                  >
                    <i className="ri-focus-3-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Layer toggle button */}
      {!showLayerPanel && (layers.length > 0 || subLayers.length > 0) && (
        <div className="absolute top-4 right-14 z-10">
          <button
            onClick={() => setShowLayerPanel(true)}
            className="bg-white p-2 rounded-lg shadow-lg text-gray-600 hover:text-indigo-600"
            title="Show Layers"
          >
            <i className="ri-stack-line"></i>
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="absolute bottom-8 left-4 bg-white/90 text-gray-700 px-3 py-1.5 rounded text-xs z-10">
        <span className="mr-3"><i className="ri-stack-line mr-1"></i>{stats.layers} layers</span>
        <span><i className="ri-shape-line mr-1"></i>{stats.features} features</span>
      </div>

      {/* Selected Feature Info */}
      {selectedFeature && (
        <div className="absolute bottom-8 right-4 bg-white rounded-lg shadow-lg p-3 text-sm max-w-xs z-10">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-gray-900">{selectedFeature.name || 'Feature'}</span>
            <button
              onClick={() => setSelectedFeature(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="text-xs space-y-1 text-gray-600">
            <div><b>Type:</b> {selectedFeature.type}</div>
            {selectedFeature.properties && Object.entries(selectedFeature.properties).slice(0, 5).map(([key, value]) => (
              <div key={key}><b>{key}:</b> {String(value)}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

GISViewer.displayName = 'GISViewer';

export default GISViewer;
